// The Mac app's pushes of this Mac's sound (16 kHz PCM16, desktopCallAudio.ts)
// played into the call mix on the AudioContext's own clock: each push is one
// AudioBuffer started where its `pos` falls on a timeline set by the first
// push, LEAD_SEC ahead. A push due too soon (the page was busy, or the app's
// capture paused while nothing played) moves the timeline later and plays at
// once after a short silence; one due too far ahead (the two clocks drifting
// apart) is dropped and the timeline moves back; a jump in `pos` (frames the
// app dropped) is a gap, and one too long to wait for (the app dropped more
// than MAX_AHEAD_SEC because the page fell behind) starts the timeline again
// from now, like a late push, so live sound is never thrown away for it.
// Nothing here reaches ctx.destination: the mix is
// recorded, never played aloud, so the app's tap never hears it back.

export const LEAD_SEC = 0.25, LATE_SEC = 0.02, MAX_AHEAD_SEC = 2;

/** When a push of `frames` at `pos` plays (null: dropped), and the timeline after it. `expected` is the pos the
 *  last push ended at (null: none yet); a push beyond it is a jump, never drift. Pure. */
export function slot(s: { pos: number; frames: number; rate: number; base: number | null; now: number; expected?: number | null }): { at: number | null; base: number } {
  const offset = s.pos / s.rate;
  if (s.base === null) return { at: s.now + LEAD_SEC, base: s.now + LEAD_SEC - offset };
  const at = s.base + offset;
  if (at < s.now + LATE_SEC) return { at: s.now + LEAD_SEC, base: s.now + LEAD_SEC - offset };
  if (at - s.now > MAX_AHEAD_SEC) {
    const jumped = s.expected != null && s.pos > s.expected;
    if (jumped) return { at: s.now + LEAD_SEC, base: s.now + LEAD_SEC - offset };
    return { at: null, base: s.base - s.frames / s.rate };
  }
  return { at, base: s.base };
}

/** Linear interpolation from one rate to another; the first and last samples are kept. Pure. */
export function upsampleLinear(pcm: Float32Array, from: number, to: number): Float32Array {
  const n = pcm.length;
  if (n === 0 || !(from > 0) || !(to > 0)) return new Float32Array(0);
  const m = Math.max(1, Math.round((n * to) / from));
  const out = new Float32Array(m);
  const step = from / to;
  for (let j = 0; j < m; j++) {
    const x = Math.min(n - 1, j * step);
    const i = Math.floor(x);
    const b = pcm[Math.min(i + 1, n - 1)];
    out[j] = pcm[i] + (b - pcm[i]) * (x - i);
  }
  out[m - 1] = pcm[n - 1];
  return out;
}

export interface PcmFeed { push(pos: number, pcm: Int16Array, rate: number): void; stop(): void }

/** Plays each push into `out` (never ctx.destination). If the context refuses a 16 kHz buffer, every push is upsampled to its own rate from then on. */
export function pcmFeed(ctx: BaseAudioContext, out: AudioNode): PcmFeed {
  let base: number | null = null;
  let expected: number | null = null;
  let upsample = false;
  let stopped = false;
  const live = new Set<AudioBufferSourceNode>();
  const bufferOf = (f: Float32Array, rate: number): AudioBuffer | null => {
    if (!upsample) {
      try {
        const buf = ctx.createBuffer(1, f.length, rate);
        buf.getChannelData(0).set(f);
        return buf;
      } catch {
        upsample = true; // WebKit may refuse a rate this low: the context's own rate from now on
      }
    }
    try {
      const up = upsampleLinear(f, rate, ctx.sampleRate);
      const buf = ctx.createBuffer(1, up.length, ctx.sampleRate);
      buf.getChannelData(0).set(up);
      return buf;
    } catch {
      return null;
    }
  };
  return {
    push(pos, pcm, rate) {
      if (stopped || pcm.length === 0 || !(rate > 0)) return;
      const s = slot({ pos, frames: pcm.length, rate, base, now: ctx.currentTime, expected });
      base = s.base;
      expected = pos + pcm.length;
      if (s.at === null) return;
      const f = new Float32Array(pcm.length);
      for (let i = 0; i < pcm.length; i++) f[i] = pcm[i] / 32768;
      const buf = bufferOf(f, rate);
      if (!buf) return;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(out);
      live.add(src);
      src.onended = () => {
        live.delete(src);
        try { src.disconnect(); } catch { /* already */ }
      };
      src.start(s.at);
    },
    stop() {
      stopped = true;
      for (const src of live) {
        try { src.stop(); } catch { /* not started, or over */ }
        try { src.disconnect(); } catch { /* already */ }
      }
      live.clear();
    },
  };
}
